import { Slider } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon } from 'tdesign-icons-react';
import { Button, InputNumber, MessagePlugin, Select, Textarea } from 'tdesign-react';

import { Reset } from '@/components/icon';
import { AiVoiceReq, Voice } from '@/interface/aigc';
import { aiVoice } from '@/services/aigc';
import { CgiErrorCode } from '@/services/cgi';

import { AudioPlayer } from '../form-upload/core/player/audio/audio';
import styles from './index.module.less';
import VoiceList from './list';

interface AigcVoiceProps {
  data?: Voice;
  onChange: (src?: Voice) => void;
}

const DEFAULT_SPEED = 1;
const SPEED_CONFIG = {
  defaultValue: DEFAULT_SPEED,
  min: 0.5,
  max: 2,
  step: 0.01,
};

const DEFAULT_PITCH = 0;
const PITCH_CONFIG = {
  defaultValue: DEFAULT_PITCH,
  min: -12,
  max: 12,
  step: 1,
};

const EMOTION_OPTIONS = [
  { label: '自动', value: '' },
  { label: '开心', value: 'happy' },
  { label: '悲伤', value: 'sad' },
  { label: '愤怒', value: 'angry' },
  { label: '害怕', value: 'fearful' },
  { label: '厌恶', value: 'disgusted' },
  { label: '惊讶', value: 'surprised' },
  { label: '平静', value: 'calm' },
];

const AigcVoiceComponent: React.FC<AigcVoiceProps> = (props) => {
  const { data, onChange } = props;
  const [speed, setSpeed] = useState(DEFAULT_SPEED); // 语速
  const [pitch, setPitch] = useState(DEFAULT_PITCH); // 音调
  const [word, setWord] = useState<string>('');
  const [lastWord, setLastWord] = useState<string>('');
  const [lastData, setLastData] = useState<Voice>();
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const audioRef = useRef<any>(null);

  useEffect(() => {
    if (data?.speed !== undefined) {
      setSpeed(data.speed);
    }
  }, [data?.speed]);

  useEffect(() => {
    if (data?.pitch !== undefined) {
      setPitch(data.pitch);
    }
  }, [data?.pitch]);

  const handleSpeedChange = (value: number) => {
    setSpeed(value);
    if (data) {
      const newData = { ...data, speed: value };
      onChange(newData);
    }
  };

  const handlePitchChange = (value: number) => {
    setPitch(value);
    if (data) {
      const newData = { ...data, pitch: value };
      onChange(newData);
    }
  };

  const handleEmotionChange = (value: string) => {
    if (data) {
      const newData = { ...data, emotion: value };
      onChange(newData);
    }
  };

  const handleCancel = () => {
    onChange();
  };

  const handlePlay = async () => {
    if (!data || word === '') {
      return;
    }

    if (JSON.stringify(data) === JSON.stringify(lastData) && word === lastWord && audioUrl) {
      audioRef.current?.triggerPlay();
      return;
    }

    setLoading(true);
    const postData: AiVoiceReq = {
      voiceInfo: data,
      text: word,
    };
    const { code, data: resData, message } = await aiVoice(postData);
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || '试听失败，请点击重试',
      });
      setLoading(false);
      return;
    }

    setAudioUrl(resData);
    setLastData(data);
    setLastWord(word);
    setTimeout(() => {
      setLoading(false);
      audioRef.current?.triggerPlay();
    }, 500);
  };

  return (
    <div className={styles.content}>
      <div className={styles.left}>
        {/* 已选音色及参数设置 */}
        {data && <div className={styles.setting}>
          <div className={styles.group}>
            <div className={styles.title}>已选择音色</div>
            <div className={styles.voice}>
              <div className={styles.header}>
                {data.voiceName || '未命名'}
                <CloseIcon onClick={handleCancel} />
              </div>
              {(data.tag || []).length > 0 && <div className={styles.tags}>
                {(data.tag || []).map((e, i) => <div key={i} className={styles.tag}>{e}</div>)}
              </div>}
            </div>
          </div>

          <div className={styles.group}>
            <div className={styles.title}>情绪</div>
            <Select
              options={EMOTION_OPTIONS}
              className={styles.select}
              value={data.emotion || ''}
              onChange={v => handleEmotionChange(v as string)}
            />
          </div>

          <div className={styles.group}>
            <div className={styles.header}>
              <div className={styles.title}>
                语速
                <Reset onClick={() => handleSpeedChange(DEFAULT_SPEED)} />
              </div>
              <InputNumber
                value={speed}
                onChange={v => handleSpeedChange(v as number)}
                {...SPEED_CONFIG}
                theme={'column'}
                className={styles.inputNumber}
                allowInputOverLimit={false}
              />
            </div>
            <Slider
              value={speed}
              onChange={v => handleSpeedChange(v as number)}
              {...SPEED_CONFIG}
              keyboard={false}
              tooltip={{ open: false }}
              className={styles.slider}
            />
          </div>

          <div className={styles.group}>
            <div className={styles.header}>
              <div className={styles.title}>
                声调
                <Reset onClick={() => handlePitchChange(DEFAULT_PITCH)} />
              </div>
              <InputNumber
                value={pitch}
                onChange={v => handlePitchChange(v as number)}
                {...PITCH_CONFIG}
                theme={'column'}
                className={styles.inputNumber}
                allowInputOverLimit={false}
              />
            </div>
            <Slider
              value={pitch}
              onChange={v => handlePitchChange(v as number)}
              {...PITCH_CONFIG}
              keyboard={false}
              tooltip={{ open: false }}
              className={styles.slider}
            />
          </div>
        </div>}

        {/* 试听 */}
        <Textarea
          value={word}
          maxlength={300}
          autosize={{ minRows: 5, maxRows: 5 }}
          placeholder="输入待试听的文字"
          onChange={e => setWord(e)}
          className={styles.textarea}
        />
        <Button
          theme='default'
          onClick={handlePlay}
          loading={loading}
          style={{ flexShrink: 0 }}
        >
          <div className={styles.btnText}>
            <div className={styles.play}>
              <AudioPlayer
                url={audioUrl}
                size={20}
                playRef={audioRef}
                disableClick={true}
              />
            </div>
            点击试听
          </div>
        </Button>
      </div>

      {/* 音色列表 */}
      <div className={styles.voiceList}>
        <VoiceList
          selectedVoice={data}
          getSelected={onChange}
        />
      </div>
    </div>
  );
};

export default AigcVoiceComponent;
